import { createAsyncThunk } from "@reduxjs/toolkit"
import { push, replace } from "redux-first-history"

import { ErrorsActions } from "../../../Redux/redux/errors.slice"
import { bindPendingProviderBanner } from "../../../Utils/anotherProviderBanner"
import { isOnboardingFinished } from "../../../Utils/onboarding/isOnboardingFinished"
import { getApiErrorMessage, handleApiError } from "../../../Utils/thunkHelpers"
import utils, { createAccountStepPath, onboardingStepPath } from "../../../Utils/utils"
import { setShowModalPremiumFeatures, updateProfile } from "../../LoginPage/redux/auth.actions"
import { AUTH_SLICE_NAME } from "../../LoginPage/redux/auth.constants"
import * as services from "../services"
import {
  attachPaymentSuccess,
  changeNextCreateAccountStep,
  createBillingDescriptorSuccess,
  setDemoTourStep,
  userSetProp
} from "./user.actions"
import { USER_SLICE_NAME } from "./user.constants"

/* ------------------------------------------------------------------ *
 * Shared helpers
 * ------------------------------------------------------------------ */

const selectUserState = (getState) => getState()[USER_SLICE_NAME]

const selectUserInfo = (state) => state[AUTH_SLICE_NAME].userInfo

/**
 * Standard failure tail: hand the slice the human-readable message and raise the global
 * notification. `silent` is the old `withoutErrorNotification` flag.
 */
const rejectApiError = async (response, { dispatch, rejectWithValue }, { silent = false } = {}) => {
  const errorMessage = await getApiErrorMessage(response)
  if (!silent) await handleApiError(dispatch, response)
  return rejectWithValue(errorMessage)
}

/**
 * Failure tail of the hand-written sagas: they notified with the raw response body and
 * left the slice's error fields alone.
 */
const rejectRawError = (response, { dispatch, rejectWithValue }) => {
  dispatch(
    ErrorsActions.errorSave(response.status === 500 ? "Server response error" : response.data)
  )
  return rejectWithValue()
}

/**
 * Failure shape of the profile / password / e-mail endpoints: either a `{ message: … }`
 * envelope, or a DRF field-error map whose *first value* is what the forms render
 * (`MyProfile`, `SecurityPage` and `ModalChangEmail` all destructure that shape).
 */
const readProfileError = (response) => {
  if (!response?.data) return "some error"
  if (response.data?.message) return response.data.message

  const values = Object.values(response.data)
  return values.length ? values[0] : null
}

/* ------------------------------------------------------------------ *
 * Profile
 * ------------------------------------------------------------------ */

export const getUserInfo = createAsyncThunk(
  `${USER_SLICE_NAME}/get_user_info`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.getUserInfo(params)
    if (!response.ok || !response.data)
      return rejectWithValue({ errors: readProfileError(response) })

    dispatch(updateProfile({ ...response.data }))
    return response.data
  }
)

export const userSave = createAsyncThunk(
  `${USER_SLICE_NAME}/user_save`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.updateUserInfo(params)
    if (!response.ok || !response.data)
      return rejectWithValue({ errors: readProfileError(response) })
    dispatch(getUserInfo())
    if (params?.user_id) return { data: response.data, user_id: true }
    dispatch(updateProfile({ ...response.data }))
    return response.data
  }
)

export const userSaveAvatar = createAsyncThunk(
  `${USER_SLICE_NAME}/user_save_avatar`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.updateUserInfo(params)
    if (!response.ok || !response.data)
      return rejectWithValue({ errors: readProfileError(response) })

    dispatch(updateProfile({ ...response.data }))
    return response.data
  }
)

export const getUserInfoByAdmin = createAsyncThunk(
  `${USER_SLICE_NAME}/get_user_info_by_admin`,
  async (params, thunkApi) => {
    const response = await services.getUserInfoByAdmin(params)
    if (!response.ok) return rejectApiError(response, thunkApi)
    return response.data
  }
)

export const trackEvent = createAsyncThunk(
  `${USER_SLICE_NAME}/track_event`,
  async (params, thunkApi) => {
    const userInfo = selectUserInfo(thunkApi.getState())
    const response = await services.trackEvent(params)
    if (!response.ok || !response.data) return rejectRawError(response, thunkApi)

    if (window.dataLayer && response.data?.new_event) {
      window.dataLayer.push({ event: params?.event_type, user_id: userInfo?.id })
    }
  }
)

/* ------------------------------------------------------------------ *
 * Password
 * ------------------------------------------------------------------ */

export const changePassword = createAsyncThunk(
  `${USER_SLICE_NAME}/change_password`,
  async (params, { rejectWithValue }) => {
    const response = await services.changePassword(params)
    if (!response.ok || !response.data)
      return rejectWithValue({ errors: readProfileError(response) })

    return response.data.message
  }
)

export const sendCodePassword = createAsyncThunk(
  `${USER_SLICE_NAME}/send_code_password`,
  async (email, { rejectWithValue }) => {
    const response = await services.sendCodePassword(email)
    if (!response.ok || !response.data)
      return rejectWithValue({ errors: readProfileError(response) })

    return response.data.message
  }
)

export const createNewPassword = createAsyncThunk(
  `${USER_SLICE_NAME}/create_new_password`,
  async (params, { rejectWithValue }) => {
    const response = await services.createNewPassword(params)
    if (!response.ok || !response.data)
      return rejectWithValue({ errors: readProfileError(response) })

    return response.data.message
  }
)

export const setPassword = createAsyncThunk(
  `${USER_SLICE_NAME}/set_password`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.createNewPassword(params)
    if (!response?.data) return rejectWithValue({ errors: "some error" })

    if (!response.ok) {
      const values = Object.values(response.data)
      return rejectWithValue({ errors: values.length ? values[0] : null })
    }

    dispatch(getUserInfo())
    return response.data
  }
)

/* ------------------------------------------------------------------ *
 * Email & language
 * ------------------------------------------------------------------ */

export const changeEmails = createAsyncThunk(
  `${USER_SLICE_NAME}/change_emails`,
  async (params, { rejectWithValue }) => {
    const response = await services.changeEmails(params)
    if (!response.ok || !response.data)
      return rejectWithValue({ errors: readProfileError(response) })

    return response.data.message
  }
)

export const confirmEmails = createAsyncThunk(
  `${USER_SLICE_NAME}/confirm_emails`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.confirmEmails(params)
    if (!response.ok || !response.data)
      return rejectWithValue({ errors: readProfileError(response) })

    dispatch(updateProfile({ ...response.data }))
    return response.data
  }
)

export const changeLanguage = createAsyncThunk(
  `${USER_SLICE_NAME}/change_language`,
  async (language, { dispatch, rejectWithValue }) => {
    const response = await services.changeLanguage(language)
    if (!response.ok || !response.data)
      return rejectWithValue({ errors: readProfileError(response) })

    dispatch(updateProfile({ ...response.data }))
    return response.data
  }
)

export const changeEmailOnboarding = createAsyncThunk(
  `${USER_SLICE_NAME}/change_email_onboarding`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.changeEmailOnboarding(params)

    if (!response?.ok) {
      const errors =
        response?.data?.message?.detail ||
        response?.data?.message ||
        response?.data?.detail ||
        response?.data?.new_email?.[0] ||
        response?.data?.new_email ||
        response?.data
      return rejectWithValue({ errors })
    }

    const result = await dispatch(getUserInfo())
    if (!getUserInfo.fulfilled.match(result)) {
      return rejectWithValue({ errors: "User refresh failed" })
    }
  }
)

/* ------------------------------------------------------------------ *
 * Onboarding
 * ------------------------------------------------------------------ */

export const sendOnboardingForm = createAsyncThunk(
  `${USER_SLICE_NAME}/send_onboarding_form`,
  async (data, thunkApi) => {
    const response = await services.sendOnboardingForm(data)
    if (!response.ok) return rejectApiError(response, thunkApi)

    thunkApi.dispatch(push(onboardingStepPath[3]))
    return response.data
  }
)

export const changeOnboardingStep = createAsyncThunk(
  `${USER_SLICE_NAME}/change_onboarding_step`,
  async (params, thunkApi) => {
    const { dispatch, getState } = thunkApi
    const userInfo = selectUserInfo(getState())
    const response = await services.updateUserInfo(params)
    if (!response.ok || !response.data) return rejectRawError(response, thunkApi)

    dispatch(updateProfile(response.data))
    if (!userInfo?.is_created_by_invitation && onboardingStepPath[params["next_step"]]) {
      dispatch(replace(onboardingStepPath[params["next_step"]]))
    }
    if (userInfo?.is_created_by_invitation && createAccountStepPath[params["next_step"] - 2]) {
      dispatch(replace(createAccountStepPath[params["next_step"] - 2]))
    }
  }
)

export const getMaxCreateSteps = createAsyncThunk(
  `${USER_SLICE_NAME}/get_max_create_steps`,
  async (_, thunkApi) => {
    const response = await services.getMaxCreateSteps()
    if (!response.ok) return rejectApiError(response, thunkApi)
    return response.data
  }
)

/* ------------------------------------------------------------------ *
 * Business accounts
 * ------------------------------------------------------------------ */

export const createBusinessAccount = createAsyncThunk(
  `${USER_SLICE_NAME}/create_business_account`,
  async (data, { dispatch, getState, rejectWithValue }) => {
    const state = getState()
    const userInfo = selectUserInfo(state)
    const nextCreateAccountStep = selectUserState(getState).nextCreateAccountStep
    const response = await services.createBusinessAccount(data)

    if (!response.ok || !response.data) {
      // The only failure in this module that also raises the global notification.
      if (response.data?.message) dispatch(ErrorsActions.errorSave(response.data.message))
      return rejectWithValue({ errors: readProfileError(response) })
    }

    bindPendingProviderBanner(response?.data?.id)

    if (!userInfo?.["is_created_first_business_account"] && !nextCreateAccountStep) {
      dispatch(
        updateProfile({
          business_account: { ...response?.data },
          is_created_first_business_account: true,
          current_business_account_id: response?.data?.id,
          next_step: 4
        })
      )
      await services.updateCompletedStep({ step: 1, business_account_id: response?.data?.id })
      dispatch(replace(onboardingStepPath[4]))
    } else {
      dispatch(changeNextCreateAccountStep(2))
      await services.updateCompletedStep({ step: 1, business_account_id: response?.data?.id })
      dispatch(push(createAccountStepPath[2]))
    }

    return response.data
  }
)

export const updateBusinessAccount = createAsyncThunk(
  `${USER_SLICE_NAME}/update_business_account`,
  async ({ skipExtraAction, ...data } = {}, thunkApi) => {
    const response = await services.updateBusinessAccount(data)
    if (!response.ok) return rejectApiError(response, thunkApi)
    if (!skipExtraAction) thunkApi.dispatch(getBusinessAccountsWithRole())
    return response.data
  }
)

export const getBusinessAccount = createAsyncThunk(
  `${USER_SLICE_NAME}/get_business_account`,
  async (id, thunkApi) => {
    const response = await services.getBusinessAccount(id)
    if (!response.ok) return rejectApiError(response, thunkApi)
    return response.data
  }
)

export const getBusinessAccounts = createAsyncThunk(
  `${USER_SLICE_NAME}/get_business_accounts`,
  async (params, thunkApi) => {
    const response = await services.getBusinessAccounts(params)
    if (!response.ok) return rejectApiError(response, thunkApi)
    return response.data
  }
)

export const getBusinessAccountsWithRole = createAsyncThunk(
  `${USER_SLICE_NAME}/get_business_accounts_with_role`,
  async (_, thunkApi) => {
    const response = await services.getBusinessAccountsWithRole()
    if (!response.ok) return rejectApiError(response, thunkApi)
    return response.data
  }
)

export const deleteBusinessAccount = createAsyncThunk(
  `${USER_SLICE_NAME}/delete_business_account`,
  async (id, thunkApi) => {
    const { dispatch, getState } = thunkApi
    const userInfo = selectUserInfo(getState())

    const response = await services.deleteBusinessAccount(id)
    if (!response.ok) return rejectRawError(response, thunkApi)

    dispatch(getBusinessAccounts())
    dispatch(push("/dashboard"))

    if (
      userInfo.last_used_business_account_id !== null &&
      userInfo.last_used_business_account_id !== userInfo.current_business_account_id
    ) {
      dispatch(userSave({ current_business_account_id: userInfo.last_used_business_account_id }))
      dispatch(
        updateProfile({ current_business_account_id: userInfo.last_used_business_account_id })
      )
    }
  }
)

export const changeBusinessAccount = createAsyncThunk(
  `${USER_SLICE_NAME}/change_business_account`,
  async (businessAccountId, { dispatch, rejectWithValue }) => {
    const response = await services.updateUserInfo({
      current_business_account_id: businessAccountId
    })
    if (!response.ok || !response.data) {
      return rejectWithValue(response.data?.message || "Failed to change business account")
    }

    await dispatch(getUserInfo())
    return response.data
  }
)

export const getBusinessInfo = createAsyncThunk(
  `${USER_SLICE_NAME}/get_business_info`,
  async (params, thunkApi) => {
    const response = await services.getBusinessInfo(params)
    if (!response.ok) return rejectApiError(response, thunkApi, { silent: true })
    return response.data
  }
)

export const getBannerInfo = createAsyncThunk(
  `${USER_SLICE_NAME}/get_banner_info`,
  async (_, thunkApi) => {
    const response = await services.getBannerInfo()
    if (!response.ok) return rejectApiError(response, thunkApi)
    return response.data
  }
)

export const updateUserPrice = createAsyncThunk(
  `${USER_SLICE_NAME}/update_user_price`,
  async (data, thunkApi) => {
    const response = await services.updateUserPrice(data)
    if (!response.ok || !response.data) return rejectApiError(response, thunkApi)
    return response.data
  }
)

export const getAutoRefundLogs = createAsyncThunk(
  `${USER_SLICE_NAME}/get_auto_refund_logs`,
  async (id, thunkApi) => {
    const response = await services.getAutoRefundLogs(id)
    if (!response.ok) return rejectApiError(response, thunkApi, { silent: true })
    return response.data
  }
)

export const getIntegrationLogs = createAsyncThunk(
  `${USER_SLICE_NAME}/get_integration_logs`,
  async (_, thunkApi) => {
    const response = await services.getIntegrationLogs()
    if (!response.ok) return rejectApiError(response, thunkApi, { silent: true })
    return response.data
  }
)

/* ------------------------------------------------------------------ *
 * Payment processors & integrations
 * ------------------------------------------------------------------ */

export const getPaymentProcessors = createAsyncThunk(
  `${USER_SLICE_NAME}/get_payment_processors`,
  async (params, thunkApi) => {
    const response = await services.getPaymentProcessors(params)
    if (!response.ok) return rejectApiError(response, thunkApi)
    return response.data
  }
)

export const connectPaymentProcessor = createAsyncThunk(
  `${USER_SLICE_NAME}/connect_payment_processor`,
  async (data, thunkApi) => {
    const response = await services.connectPaymentProcessor(data)
    if (!response.ok) return rejectApiError(response, thunkApi, { silent: true })
    if (response.data?.redirect_url) utils.goLink(response.data.redirect_url)
    thunkApi.dispatch(getUserInfo())
    return response.data
  }
)

export const disconnectPaymentProcessor = createAsyncThunk(
  `${USER_SLICE_NAME}/disconnect_payment_processor`,
  async (data, thunkApi) => {
    const response = await services.disconnectPaymentProcessor(data)
    if (!response.ok) return rejectApiError(response, thunkApi)
    if (response.data?.redirect_url) utils.goLink(response.data.redirect_url)
    return response.data
  }
)

export const disconnectPaymentProcessorExternally = createAsyncThunk(
  `${USER_SLICE_NAME}/disconnect_payment_processor_externally`,
  async (data, thunkApi) => {
    const response = await services.disconnectPaymentProcessor(data)
    if (!response.ok || !response.data) return rejectRawError(response, thunkApi)
    thunkApi.dispatch(updateProfile(response.data))
  }
)

export const adyenUploadFiles = createAsyncThunk(
  `${USER_SLICE_NAME}/adyen_upload_files`,
  async (data, thunkApi) => {
    const response = await services.adyenUploadFiles(data)
    if (!response.ok) return rejectApiError(response, thunkApi, { silent: true })
    return response.data
  }
)

export const checkMaverickDba = createAsyncThunk(
  `${USER_SLICE_NAME}/check_maverick_dba`,
  async (params, { rejectWithValue }) => {
    const response = await services.checkMaverickDba(params)
    if (!response.ok || response.data?.status !== "ok") {
      return rejectWithValue({ errors: response.data?.terminals_error || "DBA not valid" })
    }
    return response.data
  }
)

export const connectMaverickTerminals = createAsyncThunk(
  `${USER_SLICE_NAME}/connect_maverick_terminals`,
  async (data, thunkApi) => {
    const response = await services.connectMaverickTerminals(data)
    if (!response.ok) return rejectApiError(response, thunkApi)
    return response.data
  }
)

export const requestNewIntegration = createAsyncThunk(
  `${USER_SLICE_NAME}/request_new_integration`,
  async (data, thunkApi) => {
    const response = await services.requestNewIntegration(data)
    if (!response.ok) return rejectApiError(response, thunkApi)
    return response.data
  }
)

/* ------------------------------------------------------------------ *
 * Billing descriptors
 * ------------------------------------------------------------------ */

export const createBillingDescriptor = createAsyncThunk(
  `${USER_SLICE_NAME}/create_billing_descriptor`,
  async (params, thunkApi) => {
    const { dispatch, getState, rejectWithValue } = thunkApi
    const state = getState()
    const userState = selectUserState(getState)

    const { nextCreateAccountStep, nextManageEnrollmentStep, maxCreateSteps } = userState
    const redirectAfterCreateAccount = userState.redirectAfterCreateAccount
    const enrollmentId = params?.enrollmentId || null
    const enrollmentInfo = userState.enrollments?.find((el) => el.id === enrollmentId)
    const isAdmin = state?.auth?.isAdmin
    const userInfo = selectUserInfo(state)
    const business_account_id =
      params?.["descriptor_data"]?.[0]?.["account_id"] || params?.[0]?.["account_id"]
    const manage_enrollment_business_account_id = params?.ba_id

    const response = await services.createBillingDescriptor(params)

    if (!response.ok || !response.data) {
      const message = response?.data?.message
      if (message?.descriptors || message?.caid_codes) {
        dispatch(ErrorsActions.errorSave(response?.data?.error))
        return rejectWithValue(message)
      }
      if (typeof response?.data?.error === "string") {
        dispatch(ErrorsActions.errorSave(response?.data?.error))
        return rejectWithValue()
      }
      return rejectRawError(response, thunkApi)
    }

    dispatch(createBillingDescriptorSuccess(response.data))

    if (nextCreateAccountStep) {
      await services.updateCompletedStep({ step: 4, business_account_id })

      if (maxCreateSteps === 4) {
        const responseFinalize = await services.finalizeOnboarding()
        if (responseFinalize.ok && responseFinalize.data) {
          dispatch(updateProfile(responseFinalize.data))
          dispatch(getBusinessAccounts())
          dispatch(replace(redirectAfterCreateAccount || "/dashboard"))
          dispatch(attachPaymentSuccess())
          dispatch(setDemoTourStep(1))
          dispatch(userSetProp({ key: "nextCreateAccountStep", value: null }))
        }
      } else {
        dispatch(replace(createAccountStepPath[5]))
        if (userInfo?.is_created_by_invitation && !isOnboardingFinished(userInfo)) {
          dispatch(updateProfile({ next_step: 7 }))
          dispatch(userSave({ next_step: 7 }))
        }
      }
    } else if (nextManageEnrollmentStep) {
      if (enrollmentInfo?.name && !enrollmentInfo?.["is_active"]) {
        dispatch(
          userSetProp({ key: "saveResult", value: { add_enrollment: enrollmentInfo?.name } })
        )
      }
      dispatch(userSetProp({ key: "nextManageEnrollmentStep", value: null }))
      dispatch(
        replace(
          isAdmin
            ? `/business_settings/enrollment${
                manage_enrollment_business_account_id
                  ? `?${new URLSearchParams({
                      business_account_id: manage_enrollment_business_account_id
                    })}`
                  : ""
              }`
            : "/configure/enrollment"
        )
      )
    } else {
      await services.updateCompletedStep({ step: 4, business_account_id })
      dispatch(changeOnboardingStep({ next_step: 7 }))
      dispatch(replace(onboardingStepPath[7]))
    }

    return response.data
  }
)

export const validateBillingDescriptor = createAsyncThunk(
  `${USER_SLICE_NAME}/validate_billing_descriptor`,
  async (body, thunkApi) => {
    const { dispatch, rejectWithValue } = thunkApi
    const response = await services.validateBillingDescriptor(body)
    if (response.ok && response.data) return response.data?.validated

    const message = response?.data?.message
    if (message?.descriptors || message?.bin_codes || message?.caid_codes) {
      dispatch(ErrorsActions.errorSave(response?.data?.error))
      return rejectWithValue(message)
    }
    if (typeof response?.data?.error === "string") {
      dispatch(ErrorsActions.errorSave(response?.data?.error))
      return rejectWithValue()
    }
    return rejectRawError(response, thunkApi)
  }
)

export const updateBillingDescriptorStatus = createAsyncThunk(
  `${USER_SLICE_NAME}/update_billing_descriptor_status`,
  async (data, thunkApi) => {
    const response = await services.updateBillingDescriptorStatus(data)
    if (!response.ok) return rejectApiError(response, thunkApi)
    return response.data
  }
)

export const getBillingDescriptors = createAsyncThunk(
  `${USER_SLICE_NAME}/get_billing_descriptors`,
  async (params, thunkApi) => {
    const response = await services.getBillingDescriptors(params)
    if (!response.ok) return rejectApiError(response, thunkApi)
    return response.data
  }
)

export const getDescriptorsByEnrollment = createAsyncThunk(
  `${USER_SLICE_NAME}/get_descriptors_by_enrollment`,
  async (params, thunkApi) => {
    const response = await services.getDescriptorsByEnrollment(params)
    if (!response.ok) return rejectApiError(response, thunkApi)
    return response.data
  }
)

export const getMoreDescriptorsByEnrollment = createAsyncThunk(
  `${USER_SLICE_NAME}/get_more_descriptors_by_enrollment`,
  async (params, thunkApi) => {
    const response = await services.getDescriptorsByEnrollment(params)
    if (!response.ok) return rejectApiError(response, thunkApi)
    return response.data
  }
)

export const getAdminDescriptorsByEnrollment = createAsyncThunk(
  `${USER_SLICE_NAME}/get_admin_descriptors_by_enrollment`,
  async (params, thunkApi) => {
    const response = await services.getAdminDescriptorsByEnrollment(params)
    if (!response.ok) return rejectApiError(response, thunkApi)
    return response.data
  }
)

export const getMoreAdminDescriptorsByEnrollment = createAsyncThunk(
  `${USER_SLICE_NAME}/get_more_admin_descriptors_by_enrollment`,
  async (params, thunkApi) => {
    const response = await services.getAdminDescriptorsByEnrollment(params)
    if (!response.ok) return rejectApiError(response, thunkApi)
    return response.data
  }
)

export const updateAdminDescriptorStatus = createAsyncThunk(
  `${USER_SLICE_NAME}/update_admin_descriptor_status`,
  async (data, thunkApi) => {
    const response = await services.updateAdminDescriptorStatus(data)
    if (!response.ok) return rejectApiError(response, thunkApi)
    return response.data
  }
)

export const updateAdminDescriptorType = createAsyncThunk(
  `${USER_SLICE_NAME}/update_admin_descriptor_type`,
  async (data, thunkApi) => {
    const response = await services.updateAdminDescriptorType(data)
    if (!response.ok) return rejectApiError(response, thunkApi)
    return response.data
  }
)

export const getVisaCodesByEnrollment = createAsyncThunk(
  `${USER_SLICE_NAME}/get_visa_codes_by_enrollment`,
  async (params, thunkApi) => {
    const response = await services.getVisaCodesByEnrollment(params)
    if (!response.ok) return rejectApiError(response, thunkApi)
    return response.data
  }
)

export const getMoreVisaCodesByEnrollment = createAsyncThunk(
  `${USER_SLICE_NAME}/get_more_visa_codes_by_enrollment`,
  async (params, thunkApi) => {
    const response = await services.getVisaCodesByEnrollment(params)
    if (!response.ok) return rejectApiError(response, thunkApi)
    return response.data
  }
)

/* ------------------------------------------------------------------ *
 * Enrollments
 * ------------------------------------------------------------------ */

export const getEnrollments = createAsyncThunk(
  `${USER_SLICE_NAME}/get_enrollments`,
  async (businessAccountId, thunkApi) => {
    const response = await services.getEnrollments(businessAccountId)
    if (!response.ok) return rejectApiError(response, thunkApi)
    return response.data
  }
)

export const getAdminEnrollments = createAsyncThunk(
  `${USER_SLICE_NAME}/get_admin_enrollments`,
  async (params, thunkApi) => {
    const response = await services.getAdminEnrollments(params)
    if (!response.ok) return rejectApiError(response, thunkApi)
    return response.data
  }
)

export const getEnrollmentsWithoutDescriptors = createAsyncThunk(
  `${USER_SLICE_NAME}/get_enrollments_without_descriptors`,
  async (businessAccountId, thunkApi) => {
    const response = await services.getEnrollmentsWithoutDescriptors(businessAccountId)
    if (!response.ok) return rejectApiError(response, thunkApi)
    return response.data
  }
)

export const updateEnrollment = createAsyncThunk(
  `${USER_SLICE_NAME}/update_enrollment`,
  async (data, thunkApi) => {
    const response = await services.updateEnrollment(data)
    if (!response.ok) return rejectApiError(response, thunkApi)
    return response.data
  }
)

export const changeStatusEnrollment = createAsyncThunk(
  `${USER_SLICE_NAME}/change_status_enrollment`,
  async (data, thunkApi) => {
    const response = await services.changeStatusEnrollment(data)
    if (!response.ok) return rejectApiError(response, thunkApi)
    return response.data
  }
)

/* ------------------------------------------------------------------ *
 * Cards & payments
 * ------------------------------------------------------------------ */

export const getUsersCards = createAsyncThunk(
  `${USER_SLICE_NAME}/get_users_cards`,
  async (params, { rejectWithValue }) => {
    const response = await services.getUsersCards(params)
    if (!response.ok || !response.data)
      return rejectWithValue({ errors: readProfileError(response) })

    return response.data
  }
)

export const deleteUserCard = createAsyncThunk(
  `${USER_SLICE_NAME}/delete_user_card`,
  async (params, thunkApi) => {
    const response = await services.deleteCard(params)
    if (!response.ok) return rejectRawError(response, thunkApi)
    return params
  }
)

export const updateDefaultCard = createAsyncThunk(
  `${USER_SLICE_NAME}/update_default_card`,
  async (data, thunkApi) => {
    const response = await services.updateDefaultCard(data)
    if (!response.ok) return rejectApiError(response, thunkApi)
    return response.data
  }
)

export const attachPayment = createAsyncThunk(
  `${USER_SLICE_NAME}/attach_payment`,
  async (params, thunkApi) => {
    const { dispatch, getState } = thunkApi
    const userInfo = selectUserInfo(getState())
    const redirectAfterCreateAccount = selectUserState(getState).redirectAfterCreateAccount

    const response = await services.attachPayment(params)
    if (!response.ok || !response.data) return rejectRawError(response, thunkApi)

    const redirectUrl = response.data?.is_light_mode
      ? redirectAfterCreateAccount
      : createAccountStepPath[6]

    if (!isOnboardingFinished(userInfo) && response.data?.onboarding_finished) {
      window.dataLayer = window?.dataLayer || []
      window.dataLayer.push({ event: "account_created", email: response.data.email })
      dispatch(trackEvent({ event_type: "fifth_step" }))
    }

    await services.updateCompletedStep({
      step: 5,
      business_account_id: response.data?.current_business_account_id
    })
    dispatch(userSave({ next_step: 8 }))
    dispatch(updateProfile(response.data))
    dispatch(getBusinessAccounts())
    dispatch(replace(redirectUrl))
    // TODO: will return when have new demo
    // dispatch(setDemoTourStep(1))
  }
)

/* ------------------------------------------------------------------ *
 * Premium features (seems like it's broken and dead code)
 * ------------------------------------------------------------------ */

export const getPremiumFeatures = createAsyncThunk(
  `${USER_SLICE_NAME}/get_premium_features`,
  async (params, thunkApi) => {
    const response = await services.getPremiumFeatures(params)
    if (!response.ok) return rejectApiError(response, thunkApi)
    return response.data
  }
)

export const declinePremiumFeatures = createAsyncThunk(
  `${USER_SLICE_NAME}/decline_premium_features`,
  async (_, thunkApi) => {
    const response = await services.declinePremiumFeatures()
    if (!response.ok) return rejectApiError(response, thunkApi)

    thunkApi.dispatch(setShowModalPremiumFeatures(false))
    return response.data
  }
)

// Demo data toggle

export const demoDataToggle = createAsyncThunk(
  `${USER_SLICE_NAME}/demo_data_toggle`,
  async (isShown, { dispatch, rejectWithValue }) => {
    const response = await services.updateDemoDataVisibility({ is_show_demo_data: isShown })
    if (!response.ok) return rejectWithValue()

    await dispatch(getUserInfo())
    return isShown
  }
)
